import concat from 'lodash/concat';
import map from 'lodash/map';
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Blocks from '../Blocks';
import Picture from '../Picture';

import './page.scss';

const touchEnabled =
  typeof window !== 'undefined' && typeof window.PointerEvent === 'undefined';

let touchStartScreenX = null;

class Page extends React.PureComponent {
  constructor(props) {
    super(props);
    this.root = null;
    if (touchEnabled) {
      this.handleTouchstart = this.handleTouchstart.bind(this);
      this.handleTouchmove = this.handleTouchmove.bind(this);
      this.handleTouchcancel = this.handleTouchcancel.bind(this);
      this.handleTouchend = this.handleTouchend.bind(this);
    }
  }

  componentDidMount() {
    if (touchEnabled && this.root) {
      this.root.addEventListener('touchstart', this.handleTouchstart);
      this.root.addEventListener('touchmove', this.handleTouchmove);
      this.root.addEventListener('touchcancel', this.handleTouchcancel);
      this.root.addEventListener('touchend', this.handleTouchend);
    }
  }

  componentWillUnmount() {
    if (touchEnabled && this.root) {
      this.root.removeEventListener('touchstart', this.handleTouchstart);
      this.root.removeEventListener('touchmove', this.handleTouchmove);
      this.root.removeEventListener('touchcancel', this.handleTouchcancel);
      this.root.removeEventListener('touchend', this.handleTouchend);
    }
  }

  minimumTouchmove() {
    const { swipeThreshold } = this.props;
    if (this.root) {
      const { width } = this.root.getBoundingClientRect();
      return Math.floor(width * swipeThreshold);
    }
    return 13;
  }

  handleTouchstart(event) {
    if (!touchStartScreenX && event.changedTouches) {
      const { screenX } = event.changedTouches[0];
      touchStartScreenX = screenX;
      this.props.setLeftOffset(0);
    }
  }

  handleTouchmove(event) {
    if (touchStartScreenX && event.changedTouches) {
      const { screenX } = event.changedTouches[0];
      if (Math.abs(touchStartScreenX - screenX) > this.minimumTouchmove()) {
        this.props.setLeftOffset(screenX - touchStartScreenX);
      }
    }
  }

  handleTouchcancel() {
    if (touchStartScreenX) {
      touchStartScreenX = null;
      this.props.setLeftOffset(0);
    }
  }

  handleTouchend(event) {
    if (touchStartScreenX && event.changedTouches) {
      const { screenX } = event.changedTouches[0];
      if (Math.abs(touchStartScreenX - screenX) > this.minimumTouchmove()) {
        if (touchStartScreenX > screenX) {
          this.props.handleNextClick(event);
        } else {
          this.props.handlePreviousClick(event);
        }
      }
      this.handleTouchcancel();
    }
  }

  renderBackgroundImages() {
    const { featured, sketches } = this.props;
    return concat(
      map(sketches, (filename, index) => {
        const percentage = (100 / (sketches.length - 1)) * index;
        const props = {
          className: 'background',
          filepath: `sketches/${filename}`,
          key: filename,
          scale: [
            {
              atSize: '768w',
              width: 768,
            },
            {
              atSize: '828w',
              width: 828,
            },
            {
              atSize: '1024w',
              width: 1024,
            },
            {
              atSize: '1366w',
              width: 1366,
            },
            {
              atSize: '1600w',
              width: 1600,
            },
            {
              atSize: '1920w',
              width: 1920,
            },
          ],
          sources: [],
          style: {
            top: `${percentage}%`,
            minHeight: '100vh',
            transform: `translateY(-${percentage}%)`,
            opacity: 0.145,
          },
        };
        return <Picture {...props} />;
      }),
      map(featured, feature => <Picture {...feature} key={feature.filepath} />)
    );
  }

  render() {
    const {
      blocks,
      children,
      className,
      isSelected: selected,
      slug,
      style,
      tag: Tag,
    } = this.props;
    return (
      <Tag
        className={cx({ [className]: className, selected })}
        id={slug}
        ref={node => {
          this.root = node;
        }}
        style={style}
      >
        <div className="page">
          {this.renderBackgroundImages()}
          <div className="text">{children}</div>
          <Blocks blocks={blocks} />
        </div>
      </Tag>
    );
  }
}

Page.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.node,
  ]),
  featured: PropTypes.arrayOf(PropTypes.object),
  handleNextClick: PropTypes.func.isRequired,
  handlePreviousClick: PropTypes.func.isRequired,
  setLeftOffset: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
  sketches: PropTypes.arrayOf(PropTypes.string),
  slug: PropTypes.string.isRequired,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  swipeThreshold: PropTypes.number,
  tag: PropTypes.string,
};

Page.defaultProps = {
  blocks: [],
  children: null,
  className: undefined,
  featured: [],
  isSelected: false,
  sketches: [],
  style: {},
  swipeThreshold: 0.384, // decimal percent
  tag: 'article',
};

export default Page;



// WEBPACK FOOTER //
// ./src/components/Page/index.js